import { APIContext, useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Container,
  Divider,
  LinkButton,
  ProgressLoader,
  Radio,
  RadioGroupFormikField,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { TextField } from "@certn/cascadia-react/components/inputs/text-field/TextField";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { useUser } from "../../../../../hooks/useUser";
import { LeverIntegrationContext } from "../../LeverIntegrationContext";
import { LeverIntegrationStepLayout } from "../LeverIntegrationStepLayout";
import { DefaultPackageWhatsThis } from "../DefaultPackageWhatsThis";
import { WebhookSignatureWhatsThis } from "../WebhookSignatureWhatsThis";

export function FurtherDetailsStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { atsIntegrationData, triggerStages } = useContext(
    LeverIntegrationContext
  );
  const validationSchema = object().shape({
    webhook_signature_token: string().required().label(t("Webhook signature")),
    default_package_id: string().required().label(t("Default package")),
    customer_group_id: string().required().label(t("Customer group")),
  });
  const { data: packages, isLoading: isLoadingPackages } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );
  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );
  const { details: user } = useUser();
  const { domain } = useContext(APIContext);

  const { submit } = useFormikSubmit(
    "/api/internal/client-portal/ats-integrations/create/",
    {
      onSuccess: () => {
        showToast(t("Integration set up successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  const webhookUrl = `${domain}/api/external/ats/lever/${user?.client_account?.id}/check/create/`;
  const copyToClipBoard = () => {
    navigator.clipboard.writeText(webhookUrl);
    showToast(t("URL copied to clipboard"), { type: "success" });
  };

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={(values, helpers) =>
        submit(
          {
            is_active: true,
            report_url_type: values.report_url_type,
            lever_integration: {
              api_key: atsIntegrationData.api_key,
              default_package_id: values.default_package_id,
              customer_group_id: values.customer_group_id,
              signature_token: values.webhook_signature_token,
              trigger_stage_id: values.trigger_stage_id,
            },
          },
          helpers
        )
      }
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, values }) => {
        return (
          <LeverIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="lever-details-page-outermost-container"
              className={styles.container}
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 2,
                      stepCount: 2,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", { integration: "Lever" })}
                </Text>
                <ProgressLoader floating value={100} />
              </Stack>
              <Stack gap="space-4">
                <Text style="global/heading-medium">
                  {t("Details to add to your {{integration}} app", {
                    integration: "Lever",
                  })}
                </Text>
                <Stack orientation="horizontal" align="end" gap="space-2">
                  <TextField
                    className={styles.textFieldSpacing}
                    testID="webhook-url"
                    label={t("Webhook URL")}
                    value={webhookUrl}
                    isDisabled
                    isRequired
                  />
                  <BasicButton
                    onPress={copyToClipBoard}
                    type="button"
                    size="medium"
                    testID="copy-link-button"
                    leftIcon="link"
                  >
                    {t("Copy link")}
                  </BasicButton>
                </Stack>
              </Stack>
              <Divider marginTop="space-6" />
              <div className={styles.mainContentContainer}>
                <section>
                  <TextInputFormikField
                    name="webhook_signature_token"
                    label={t("Webhook signature token")}
                    testID="webhook-signature-token-input"
                    isRequired
                    info={<WebhookSignatureWhatsThis />}
                  />
                </section>
                <section>
                  <RadioGroupFormikField
                    label={t("Report URL type")}
                    testID="report-url-type"
                    name="report_url_type"
                    orientation="horizontal"
                    isRequired
                    defaultValue="DIRECT_PDF_URL"
                  >
                    <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                      {t("PDF URL")}
                    </Radio>
                    <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                      {t("Client Portal URL (Authenticated)")}
                    </Radio>
                  </RadioGroupFormikField>
                </section>
                <Divider />
                <section>
                  <SelectFormikField
                    name="trigger_stage_id"
                    label={t("Trigger stage")}
                    placeholder={t("Please select")}
                    isRequired
                    testID="ats-integration-trigger-stage-select"
                  >
                    {triggerStages.map(({ id, text }) => (
                      <SelectItem key={id} id={id}>
                        {text}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <section>
                  <SelectFormikField
                    label={t("Default package")}
                    testID="default-package"
                    name="default_package_id"
                    isRequired
                    isLoading={isLoadingPackages}
                    info={<DefaultPackageWhatsThis />}
                  >
                    {packages?.map(({ id, name }) => (
                      <SelectItem key={id} id={id} textValue={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <LinkButton
                  onPress={() => window.open("/packages/create/")}
                  testID="lever-create-package-link"
                >
                  {t("Create a new package")}
                </LinkButton>
                <section>
                  <SelectFormikField
                    label={t("Customer group")}
                    testID="customer-group"
                    name="customer_group_id"
                    isRequired
                    isLoading={isLoadingCustomerGroups}
                  >
                    {customerGroups?.map(({ id, name }) => (
                      <SelectItem key={id} id={id} textValue={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                  <LinkButton
                    onPress={() => window.open("/groups/create/")}
                    testID="lever-create-groups-link"
                  >
                    {t("Create a new group")}
                  </LinkButton>
                </section>
              </div>
            </Container>
          </LeverIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  textFieldSpacing: css({
    width: "440px !important",
  }),
};
